import React, { useEffect, useState } from 'react';
import './disclaimer.css';
import usePageMeta from "../../../hooks/usePageMeta";

const travelDisclaimerTerms = [
  {
    number: 1,
    title: 'Travel Services',
    paragraphs: [
      <>
        MakeBeeTrip Private Limited operates as a{' '}
        <strong>B2C travel platform and travel service facilitator</strong>.
      </>,
      'We provide access to and assistance with various travel services, including:',
    ],
    bullets: [
      'Flight Tickets',
      'Hotel Bookings',
      'Holiday Packages',
      'Cab & Airport Transfers',
      'Bus Bookings',
      'Visa Assistance',
    ],
    after: [
      'Many of these services are provided or fulfilled by third-party airlines, hotels, transportation companies, tour operators, visa service providers and other travel suppliers.',
    ],
  },
  {
    number: 2,
    title: 'Availability and Pricing',
    paragraphs: [
      'All travel services, prices, fares, hotel rooms, seats and packages displayed on our website are subject to availability.',
      'Prices may change without prior notice due to factors including:',
    ],
    bullets: [
      'Airline fare changes',
      'Hotel availability',
      'Supplier pricing',
      'Seasonal demand',
      'Currency fluctuations',
      'Taxes and government charges',
      'Changes in travel dates or passenger details',
    ],
    after: [
      'The final price applicable to a booking will be confirmed at the time of booking.',
    ],
  },
  {
    number: 3,
    title: 'Flight Information',
    paragraphs: [
      'Flight schedules, timings, routes, baggage allowances, fares and other flight-related information may be provided by airlines or travel suppliers.',
      'Airlines may change, delay, reschedule or cancel flights.',
      'MakeBeeTrip is not responsible for operational decisions made independently by airlines.',
      'Customers should check their flight status and travel requirements before departure.',
    ],
  },
  {
    number: 4,
    title: 'Hotel Information',
    paragraphs: [
      'Hotel descriptions, photographs, facilities, room information, ratings and other hotel details may be provided by hotels or third-party suppliers.',
      'Actual facilities, services and availability may vary.',
      "Hotel bookings are subject to the hotel's applicable terms, conditions and policies.",
    ],
  },
  {
    number: 5,
    title: 'Holiday Packages',
    paragraphs: [
      'Holiday packages may include services provided by multiple suppliers.',
      'The itinerary, hotel, transportation, sightseeing and activities may be subject to availability and operational conditions.',
      'Changes may sometimes be required due to weather, local conditions, supplier availability, government restrictions or other circumstances beyond reasonable control.',
      'Any applicable changes will be communicated to the customer wherever reasonably possible.',
    ],
  },
  {
    number: 6,
    title: 'Visa Disclaimer',
    paragraphs: [
      'MakeBeeTrip may provide visa assistance and documentation support for selected destinations.',
    ],
    emphasis:
      'Visa approval is solely at the discretion of the relevant embassy, consulate, immigration authority or visa-issuing authority.',
    after: [
      'MakeBeeTrip does not guarantee:',
    ],
    bullets: [
      'Visa approval',
      'Visa issuance',
      'Processing time',
      'Immigration clearance',
      'Entry into a destination country',
    ],
  },
  {
    number: 7,
    title: 'Travel Documents',
    paragraphs: [
      'Customers are responsible for ensuring that they have all required travel documents, including where applicable:',
    ],
    bullets: [
      'Valid passport',
      'Visa',
      'Entry permits',
      'Travel insurance',
      'Required certificates',
      'Tickets and booking confirmations',
      'Other destination-specific documents',
    ],
    after: [
      'MakeBeeTrip is not responsible for denial of boarding, immigration refusal or entry refusal resulting from incomplete, incorrect or invalid travel documents.',
    ],
  },
  {
    number: 8,
    title: 'Third-Party Suppliers',
    paragraphs: [
      'MakeBeeTrip works with third-party travel suppliers to facilitate travel services.',
      'These suppliers may include:',
    ],
    bullets: [
      'Airlines',
      'Hotels',
      'Bus operators',
      'Cab operators',
      'Tour operators',
      'Destination management companies',
      'Visa processing partners',
      'Payment gateway providers',
    ],
    after: [
      "Each supplier may have its own terms, conditions, cancellation rules, refund policies and service standards.",
      'Customers are advised to review the applicable supplier terms before confirming a booking.',
    ],
  },
  {
    number: 9,
    title: 'Website Information',
    paragraphs: [
      'We make reasonable efforts to provide accurate and updated information on our website.',
      'However, we do not guarantee that all information will always be:',
    ],
    bullets: [
      'Complete',
      'Accurate',
      'Current',
      'Error-free',
      'Available without interruption',
    ],
    after: [
      'Travel information can change frequently, and customers should verify important travel details before departure.',
    ],
  },
  {
    number: 10,
    title: 'External Links',
    paragraphs: [
      'The MakeBeeTrip website may contain links to third-party websites.',
      'These links may be provided for convenience or additional information.',
      'MakeBeeTrip does not control and is not responsible for the content, availability, security or privacy practices of third-party websites.',
    ],
  },
  {
    number: 11,
    title: 'Payment Disclaimer',
    paragraphs: [
      'Online payments may be processed through third-party payment gateway providers.',
      'MakeBeeTrip does not ask customers to provide confidential authentication information such as:',
    ],
    bullets: [
      'UPI PIN',
      'Card PIN',
      'Internet Banking Password',
      'Payment OTP',
    ],
    after: [
      'Customers should make payments only through official MakeBeeTrip payment channels.',
      <>
        Please refer to our{' '}
        <a href="/payment-security">
          <strong>Payment Security</strong>
        </a>{' '}
        page for additional information.
      </>,
    ],
  },
  {
    number: 12,
    title: 'Force Majeure',
    paragraphs: [
      'MakeBeeTrip shall not be responsible for delays, cancellations, losses or changes caused by circumstances beyond reasonable control, including:',
    ],
    bullets: [
      'Natural disasters',
      'Severe weather',
      'Government restrictions',
      'Strikes',
      'Civil disturbances',
      'War or security situations',
      'Pandemics or public health emergencies',
      'Transportation disruptions',
      'Airline or supplier operational issues',
      'Technical failures',
      'Other unforeseen circumstances',
    ],
  },
  {
    number: 13,
    title: 'Customer Responsibility',
    paragraphs: [
      'Customers are responsible for reviewing their booking details and ensuring that all information provided to MakeBeeTrip is accurate.',
      'This includes:',
    ],
    bullets: [
      'Passenger names',
      'Dates of birth',
      'Travel dates',
      'Passport details',
      'Contact information',
      'Visa information',
      'Pickup and drop details',
    ],
    after: [
      'Customers should carefully review all booking confirmations immediately after receiving them.',
    ],
  },
  {
    number: 14,
    title: 'Limitation of Responsibility',
    paragraphs: [
      'MakeBeeTrip will make reasonable efforts to assist customers with their travel bookings and related services.',
      "However, where a service is provided by a third-party supplier, the supplier's applicable terms and operational decisions will apply.",
      'MakeBeeTrip shall not be responsible for circumstances that are outside its reasonable control, subject to applicable law.',
    ],
  },
  {
    number: 15,
    title: 'Changes to This Disclaimer',
    paragraphs: [
      'MakeBeeTrip may update this Disclaimer from time to time to reflect changes in our services, website or applicable requirements.',
      'Any updated version will be published on this page with a revised "Last Updated" date.',
    ],
  },
  {
    number: 16,
    title: 'Contact Us',
    paragraphs: [
      'For any questions regarding this Disclaimer or our travel services, please contact:',
      <strong>MakeBeeTrip Private Limited</strong>,
      <>
        <strong>Phone:</strong> 9355471999
      </>,
      <>
        <strong>Email:</strong>{' '}
        <a href="mailto:Holidays@makebeetrip.com">
          Holidays@makebeetrip.com
        </a>
      </>,
      <>
        <strong>Website:</strong>{' '}
        <a
          href="https://makebeetrip.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://makebeetrip.com/
        </a>
      </>,
    ],
    address: (
      <>
        <strong>Registered/Mailing Address:</strong>
        <br />
        WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
        Delhi, India
      </>
    ),
    after: [
      <>
        <strong>CIN:</strong> U79110DC2026PTC471178
      </>,
    ],
  },
];

function DisclaimerSection({ section }) {
  return (
    <section
      className="mbt-disclaimer-section"
      id={`travel-disclaimer-section-${section.number}`}
    >
      <div className="mbt-disclaimer-section__heading">
        <span className="mbt-disclaimer-section__number">
          {String(section.number).padStart(2, '0')}
        </span>

        <h2>{section.title}</h2>
      </div>

      {section.paragraphs?.map((paragraph, index) => (
        <p key={`paragraph-${section.number}-${index}`}>
          {paragraph}
        </p>
      ))}

      {section.emphasis && (
        <p className="mbt-disclaimer-emphasis">
          {section.emphasis}
        </p>
      )}

      {section.bullets && (
        <ul className="mbt-disclaimer-list">
          {section.bullets.map((item, index) => (
            <li key={`bullet-${section.number}-${index}`}>
              {item}
            </li>
          ))}
        </ul>
      )}

      {section.after?.map((paragraph, index) => (
        <p key={`after-${section.number}-${index}`}>
          {paragraph}
        </p>
      ))}

      {section.address && (
        <div className="mbt-disclaimer-address">
          {section.address}
        </div>
      )}
    </section>
  );
}

export default function Disclaimer() {
  usePageMeta(
  "Disclaimer | MakeBeeTrip",
  "Disclaimer for MakeBeeTrip's travel services, including accuracy of information, third-party suppliers and limitations of liability.",
  "makebeetrip disclaimer, travel disclaimer, liability, third party suppliers, website information"
);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress =
        documentHeight > 0
          ? (scrollTop / documentHeight) * 100
          : 0;

      setScrollProgress(progress);
      setShowScrollTop(scrollTop > 500);
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mbt-disclaimer-page">
      <div
        className="mbt-disclaimer-scroll-indicator"
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="mbt-disclaimer-sticky-nav">
        <div className="mbt-disclaimer-nav-container">
          <button
            type="button"
            className="mbt-disclaimer-back-button"
            onClick={() => window.history.back()}
          >
            <span aria-hidden="true">←</span>
            Back
          </button>
        </div>
      </div>

      <header className="mbt-disclaimer-hero">
        <div className="mbt-disclaimer-hero__container">
          <div
  className="mbt-disclaimer-icon-box"
  aria-hidden="true"
>
  <svg
    viewBox="0 0 24 24"
    width="32"
    height="32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 3.5h9l3 3V20.5H6V3.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 3.5V7h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 11h6M9 14.5h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
</div>

          <span className="mbt-disclaimer-eyebrow">
            GENERAL TRAVEL DISCLAIMER
          </span>

          <h1>General Travel Disclaimer</h1>

          <p className="mbt-disclaimer-hero__description">
            Important information regarding MakeBeeTrip travel
            services, third-party suppliers, availability, pricing and
            customer responsibilities.
          </p>

          <div className="mbt-disclaimer-updated-badge">
            Last Updated: 24 September 2026
          </div>
        </div>
      </header>

      <main className="mbt-disclaimer-content">
        <article className="mbt-disclaimer-card">
          <div className="mbt-disclaimer-intro">
            <p>
              The information and services provided through{' '}
              <strong>MakeBeeTrip</strong> are intended to assist
              customers in planning and booking travel services. By
              using{' '}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>https://makebeetrip.com/</strong>
              </a>
              , you acknowledge and agree to the terms of this
              Disclaimer.
            </p>

            <div className="mbt-disclaimer-updated-badge">
              Last Updated: 24 September 2026
            </div>
          </div>

          <nav
            className="mbt-travel-disclaimer-quick-nav"
            aria-label="General travel disclaimer sections"
          >
            <div className="mbt-travel-disclaimer-quick-nav__title">
              Travel Disclaimer
            </div>

            <div className="mbt-travel-disclaimer-quick-nav__links">
              {travelDisclaimerTerms.map((section) => (
                <a
                  key={section.number}
                  href={`#travel-disclaimer-section-${section.number}`}
                >
                  {String(section.number).padStart(2, '0')}{' '}
                  {section.title}
                </a>
              ))}
            </div>
          </nav>

          {travelDisclaimerTerms.map((section, index) => (
            <React.Fragment key={section.number}>
              <DisclaimerSection section={section} />

              {index < travelDisclaimerTerms.length - 1 && (
                <hr className="mbt-disclaimer-divider" />
              )}
            </React.Fragment>
          ))}

          <div className="mbt-disclaimer-closing">
            MakeBeeTrip – Your Journey, Our Commitment.
          </div>
        </article>
      </main>

      {showScrollTop && (
        <button
          type="button"
          className="mbt-disclaimer-scroll-top"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}